import React, { useCallback, useEffect, useState } from 'react';
import {
  Form,
  Button,
  Typography,
  Space,
  notification,
  Tag,
  Row,
  Col
} from 'antd';
import TableWithSorter from '../../components/Common/TableWithSorter';
import {
  fetchCommunityLogs,
  getCommunities
} from '../../services/communityService';
import CommunitiesEmailLogsForm from './components/CommunityMailLogsForm.jsx';
import { defaultPageSize, sortOrderList } from './constant';

const { Title } = Typography;

const CommunitiesApplicationPage = () => {
  const [form] = Form.useForm();
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [withDate, setWithDate] = useState(false);
  const [communityList, setCommunityList] = useState([]);

  const [filters, setFilters] = useState({
    mailCourse: null,
    sortOrder: sortOrderList[0].value,
    startDate: null,
    endDate: null,
    received: 'all',
    sortBy: 'createdAt',
    status: null
  });
  const [logList, setEmailLogList] = useState(false);

  const columns = [
    {
      title: 'No',
      dataIndex: 'key',
      key: 'key',
      render: (value, item, index) => (pageNo - 1) * 10 + index + 1
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link">{text}</Button>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'toMail',
      key: 'toMail'
    },
    {
      title: 'Template',
      dataIndex: 'mailType',
      key: 'mailType',
      skipInjectedSorter: true,
      sorter: (a, b) =>
        a?.payments?.subscriptionId - b?.payments?.subscriptionId
    },
    {
      title: 'Status',
      dataIndex: 'hasReceived',
      key: 'hasReceived',
      render: (text, record) => {
        return renderEmailReceivedTag({
          text,
          status: record?.hasReceived
        });
      }
    },
    {
      title: 'Mail Received On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, record) => {
        return (
          <Space size="middle">
            <Button type="link">{text}</Button>
          </Space>
        );
      }
    }
  ];

  const renderEmailReceivedTag = ({ status, text }) => {
    const tagColor =
      status === true ? 'green' : status === false ? 'volcano' : 'blue';
    const tagText = text === true ? 'Received' : 'Not Received';
    return <Tag color={tagColor}>{tagText}</Tag>;
  };

  const onPaginationChange = (data) => {
    setPageNo(data.current);
    setPageSize(data.pageSize);
  };

  const onFinish = (values) => {
    setPageNo(1);

    const filterData = {
      mailCourse: values.mailCourse ?? communityList[0]?.value,
      sortBy: 'createdAt',
      received: values.received,
      mailType: values.mailType,
      startDate: withDate ? values.startDate.utc().format() : null,
      endDate: withDate ? values.endDate.utc().format() : null
    };

    setFilters(filterData);
  };

  const resetForm = () => {
    form.resetFields();
    form.submit();
  };

  const getCommunityLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        pageNo,
        pageSize,
        ...filters
      };

      const { data, error } = await fetchCommunityLogs(params);

      if (error) throw new Error(error);

      setEmailLogList(data.paginatedList);
      setTotalCount(data.totalCount);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  }, [pageNo, pageSize, filters]);
  useEffect(() => {
    if (communityList?.length > 0 && filters?.mailCourse) {
      getCommunityLogs();
    }
  }, [getCommunityLogs, communityList, filters?.mailCourse]);

  const getCommunityList = useCallback(async () => {
    try {
      setIsLoading(true);
      const values = await getCommunities({ pageNo: 1, pageSize: 1000 });
      const result = values?.data?.paginatedList?.map((community) => ({
        value: community?.code,
        text: community?.title
      }));
      const communities = result.sort((communityA, communityB) =>
        communityA?.text?.localeCompare(communityB?.text)
      );
      setCommunityList(communities);
      setFilters((prev) => {
        return {
          ...prev,
          mailCourse: communities?.[0]?.value
        };
      });
      form.setFieldsValue({ mailCourse: communities?.[0]?.value });
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(true);
    }
  }, [form]);

  useEffect(() => {
    getCommunityList();
  }, [getCommunityList]);

  return (
    <div>
      <div>
        <Row justify="space-between" align="middle">
          <Col>
            <Title>Communities Email Logs (Sendgrid)</Title>
          </Col>
        </Row>
        <CommunitiesEmailLogsForm
          onFinish={onFinish}
          resetForm={resetForm}
          form={form}
          totalCount={totalCount}
          communityList={communityList}
          withDate={withDate}
          setWithDate={setWithDate}
        />
        <TableWithSorter
          tableLayout="auto"
          scroll={{ x: 1000 }}
          expandable={{ defaultExpandAllRows: true }}
          loading={isLoading}
          dataSource={logList}
          columns={columns}
          pagination={{
            total: totalCount,
            defaultPageSize: defaultPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50']
          }}
          onChange={onPaginationChange}
        />
      </div>
    </div>
  );
};

export default CommunitiesApplicationPage;
